/**
 * EmojiPlugin.tsx
 *
 * Provides emoji picker functionality for the editor.
 * Uses emoji-picker-react for reliable emoji selection with proper scroll handling.
 *
 * Features:
 * - Emoji picker button in toolbar
 * - Insert emoji at cursor position
 * - Recent emojis saved automatically
 * - Accessible with keyboard navigation
 * - Portal rendering to avoid clipping issues
 * - Native React component (no shadow DOM issues)
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection } from 'lexical';
import { useCallback, useState, useEffect, useRef } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { EmojiClickData } from 'emoji-picker-react';
import { EmojiPickerCompact } from '@/components/ui/EmojiPickerCompact';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/utils/cn';

interface EmojiPluginProps {
  /**
   * Custom button class name
   */
  className?: string;
}

/**
 * EmojiPlugin component
 * Renders emoji picker button for toolbar with Radix Popover
 */
export function EmojiPlugin({ className = '' }: EmojiPluginProps): JSX.Element {
  const { t } = useTranslation('common');
  const [editor] = useLexicalComposerContext();
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const rafRef = useRef<number>(0);

  /**
   * Detect mobile viewport for responsive sizing
   */
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /**
   * Wait for Radix to position the popover before showing content.
   * Double RAF ensures DOM update + position calculation complete.
   */
  useEffect(() => {
    if (open) {
      setIsPositioned(false);
      // First RAF: wait for DOM update
      rafRef.current = requestAnimationFrame(() => {
        // Second RAF: wait for Radix position calculation
        rafRef.current = requestAnimationFrame(() => {
          setIsPositioned(true);
        });
      });
    } else {
      setIsPositioned(false);
    }
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [open]);

  /**
   * Insert emoji at cursor position
   */
  const handleEmojiClick = useCallback(
    (emojiData: EmojiClickData) => {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          selection.insertText(emojiData.emoji);
        }
      });

      setOpen(false);

      // Return focus to editor
      editor.focus();
    },
    [editor]
  );

  /**
   * Close picker on Escape key and return focus to editor
   */
  const handleEscapeKeyDown = useCallback(() => {
    setOpen(false);
    editor.focus();
  }, [editor]);

  // Responsive dimensions
  const pickerWidth = isMobile ? 300 : 350;
  const pickerHeight = isMobile ? 350 : 400;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Tooltip content={t('emoji_insert_tooltip')} side="bottom" disabled={open}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={cn(
              'flex items-center justify-center w-8 h-8 max-sm:w-[44px] max-sm:h-[44px] rounded hover:bg-soft-mint transition-colors',
              open && 'bg-primary-50',
              className
            )}
            aria-label={t('emoji_insert')}
          >
            <Icon name={ICONS.EMO_SMILE} size="sm" color="text-gray-600" />
          </button>
        </Popover.Trigger>
      </Tooltip>

      <Popover.Portal>
        <Popover.Content
          className={cn(
            'shadow-xl rounded-lg overflow-hidden',
            'bg-white',
            // Ensure it's above all other elements including modals/drawers
            'z-[100000]',
            // Hide until positioned to prevent flicker
            !isPositioned && 'opacity-0'
          )}
          // Position below the button
          side="bottom"
          // Align to start on desktop, center on mobile
          align={isMobile ? 'center' : 'start'}
          // Offset from the trigger button
          sideOffset={8}
          // Allow it to flip to top if not enough space below
          avoidCollisions={true}
          // Collision padding from viewport edges
          collisionPadding={isMobile ? 8 : 16}
          // Handle escape key
          onEscapeKeyDown={handleEscapeKeyDown}
        >
          {/* Only render picker after positioned to prevent initialization flicker */}
          {isPositioned && (
            <EmojiPickerCompact
              onEmojiClick={handleEmojiClick}
              width={pickerWidth}
              height={pickerHeight}
            />
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default EmojiPlugin;
